import { Trade } from '../components/TradeItem/types';
import { HedgeAccountingStage } from '../components/TradeList/enums/hedgeAccountingStage';
import { UIHedgeAmortData } from '../components/TransactionAmortization/types';

import {
  BulkJournalEntryCommand,
  BulkJournalEntryTypeAndAmount,
  BulkTransactionTitle,
  HedgeAmortData,
  JournalEntry,
  JournalEntryData,
  ReferenceJeConfig,
  TransactionEffectivenessData,
  TransactionEffectivenessPayload,
  TransactionRelation,
} from './types';

const JE_NON_ENTRY_FIELDS = new Set<keyof JournalEntryData>([
  'status_id',
  'maturity_date',
]);

export function mapApiToUI(api: HedgeAmortData): UIHedgeAmortData {
  return {
    id: `${api.transaction_id}_${api.hedge_acc_amort_id}`,
    hedge_acc_amort_id: api.hedge_acc_amort_id,
    tradeId: api.transaction_id,
    amortDate: api.amort_date,
    amortAmount: api.amort_amt,
    effectiveRate: api.amort_eff_rate,
    comment: api.amort_comment || '',
    jeTypeId: api.je_type_id,
    scheduleTransactionId: api.amort_schedule_transaction_id,
    scheduleTypeId: api.amort_schedule_type_id,
    companyEntity: api.company_entity_name || '',
    companyEntityId: api.company_entity_id || '',
    bank: api.bank_entity_name || '',
  };
}

export const getMaturityDate = (trade: Trade) => trade?.maturity_date || '';

export const getProgressPercent = (trade: Trade) => {
  const hasJe = hasMeaningfulJeData(trade?.journalEntries);

  const hasEff =
    Array.isArray(trade?.transactionEffectivenessData) &&
    trade.transactionEffectivenessData.length > 0;

  const hasAmort =
    Array.isArray(trade?.hedgeAmortData) && trade.hedgeAmortData.length > 0;

  const hasHypo =
    Array.isArray(trade?.hypotheticalData) && trade.hypotheticalData.length > 0;

  const stepsTotal = 4;
  const stepsDone = [hasJe, hasEff, hasAmort, hasHypo].filter(Boolean).length;

  return Math.round((stepsDone / stepsTotal) * 100);
};

export const getStageTitle = (trade: Trade): HedgeAccountingStage => {
  const hasJe = hasMeaningfulJeData(trade?.journalEntries);

  const hasAmort =
    Array.isArray(trade?.hedgeAmortData) && trade.hedgeAmortData.length > 0;

  const hasNoHedge = Boolean(trade?.noHedgeData);

  if (!hasJe) return HedgeAccountingStage.PendingJournalEntries;
  if (!hasAmort) return HedgeAccountingStage.PendingAmortisation;
  if (!hasNoHedge) return HedgeAccountingStage.PendingNoHedgeCheck;

  return HedgeAccountingStage.DataReady;
};

export const getTitle = (trade: Trade) => {
  const je = trade?.journalEntries?.[0];
  const notional = je?.current_trade_notional;
  const ccy = je?.currency_name;
  const instrument = je?.instrument_type_name;
  const maturity = je?.maturity_date;

  const notionalText =
    typeof notional === 'number'
      ? `${ccy || ''} ${notional.toLocaleString()}`.trim()
      : '';

  return [
    notionalText,
    instrument,
    maturity ? `Maturity Date: ${maturity}` : '',
  ]
    .filter(Boolean)
    .join(' - ');
};

export const toCouponDecimal = (value: string): number | null => {
  if (!value) return null;

  const cleaned = value.replace('%', '').trim();
  const num = Number(cleaned);

  if (Number.isNaN(num)) return null;

  return num / 100;
};

export const buildEntriesFromJeRow = (
  row: JournalEntryData,
): BulkJournalEntryTypeAndAmount[] => {
  return (
    Object.entries(row) as [
      keyof JournalEntryData,
      JournalEntryData[keyof JournalEntryData],
    ][]
  )
    .filter(([key, value]) => {
      if (JE_NON_ENTRY_FIELDS.has(key)) return false;

      return typeof value === 'number' && !Number.isNaN(value);
    })
    .map(([je_type, je_amount]) => ({
      je_type,
      je_amount: Number(je_amount),
    }));
};

export const hasMeaningfulJeData = (
  journalEntries?: JournalEntry[],
): boolean => {
  if (!Array.isArray(journalEntries) || journalEntries.length === 0) {
    return false;
  }

  return journalEntries.some((je) => {
    if (!je?.je_data || typeof je.je_data !== 'object') return false;

    return Object.values(je.je_data).some((row) => {
      if (!row) return false;

      return buildEntriesFromJeRow(row).length > 0;
    });
  });
};

export const getSelectedTypesFromReferenceJeData = (
  journalEntries?: JournalEntry[],
): ReferenceJeConfig | null => {
  const referenceJournalEntry = journalEntries?.find(
    (journalEntry) => Object.keys(journalEntry.je_data ?? {}).length > 0,
  );

  if (!referenceJournalEntry) {
    return null;
  }

  const [jeDate, jeData] =
    Object.entries(referenceJournalEntry.je_data ?? {})[0] ?? [];

  if (!jeDate || !jeData) {
    return null;
  }

  const selectedTypes = buildEntriesFromJeRow(jeData).map(({ je_type }) => ({
    label: je_type,
    value: 0,
  }));

  if (!selectedTypes.length) {
    return null;
  }

  return {
    jeDate,
    selectedTypes,
  };
};

export const buildCreateCommandsFromReferenceJeMovement = (
  targetTradeId: number,
  referenceJournalEntries: JournalEntry[],
): BulkJournalEntryCommand[] => {
  return referenceJournalEntries.flatMap((je) => {
    if (!je?.je_data || typeof je.je_data !== 'object') return [];

    return Object.entries(je.je_data).flatMap(([je_date, row]) => {
      if (!row) return [];

      const entries = buildEntriesFromJeRow(row);

      if (!entries.length) return [];

      return [
        {
          trade_id: targetTradeId,
          row_status: 'new',
          je_date,
          je_status_id: Number(row.status_id ?? 1),
          je_comment: '',
          entries,
        },
      ];
    });
  });
};

export const buildDeleteCommandsFromExistingJe = (
  targetTradeId: number,
  existingJournalEntries: JournalEntry[],
): BulkJournalEntryCommand[] => {
  return existingJournalEntries.flatMap((je) => {
    if (!je?.je_data || typeof je.je_data !== 'object') return [];

    return Object.entries(je.je_data).flatMap(([je_date, row]) => {
      if (!row) return [];

      const entries = buildEntriesFromJeRow(row);

      if (!entries.length) return [];

      return [
        {
          trade_id: targetTradeId,
          row_status: 'delete',
          je_date,
          je_status_id: Number(row.status_id ?? 1),
          je_comment: '',
          entries,
        },
      ];
    });
  });
};

export const hasMeaningfulEffectivenessData = (
  effectivenessData?: TransactionEffectivenessData[],
): boolean => {
  return Array.isArray(effectivenessData) && effectivenessData.length > 0;
};

export const buildCreatePayloadsFromReferenceEffectiveness = (
  targetTradeId: number,
  referenceEffectiveness: TransactionEffectivenessData[],
): TransactionEffectivenessPayload[] => {
  return referenceEffectiveness.map((eff) => ({
    transaction: targetTradeId,
    hedge_acc_eff: eff.hedge_acc_eff_id,
    hedge_acc_eff_clean_value: !!eff.hedge_acc_eff_clean_value,
    hedge_acc_eff_cva_actual: !!eff.hedge_acc_eff_cva_actual,
    hedge_acc_eff_cva_hypo: !!eff.hedge_acc_eff_cva_hypo,
    hedge_acc_eff_cash_flows: !!eff.hedge_acc_eff_cash_flows,
    hedge_acc_eff_time_value: !!eff.hedge_acc_eff_time_value,
    hedge_acc_eff_new_date_method: !!eff.hedge_acc_eff_new_date_method,
    hedge_acc_credit_adj_values_final_balance:
      !!eff.hedge_acc_credit_adj_values_final_balance,
  }));
};

export const groupEffectivenessByTradeId = (
  effectivenessRows: TransactionEffectivenessData[],
): Record<string, TransactionEffectivenessData[]> => {
  return effectivenessRows.reduce<
    Record<string, TransactionEffectivenessData[]>
  >((acc, item) => {
    const key = String(item.transaction_id);

    if (!acc[key]) {
      acc[key] = [];
    }

    acc[key].push(item);

    return acc;
  }, {});
};

export const hasMeaningfulHypotheticalData = (
  relation: TransactionRelation | null | undefined,
): boolean => {
  return Boolean(
    relation &&
      relation.hypo_data &&
      typeof relation.hypo_data === 'object' &&
      Object.keys(relation.hypo_data).length > 0,
  );
};

export const getHypotheticalRowsFromRelation = (
  relation: TransactionRelation | null | undefined,
) => {
  if (!relation?.hypo_data || typeof relation.hypo_data !== 'object') {
    return [];
  }

  return Object.values(relation.hypo_data);
};

export const groupRelationsByTradeId = (
  relations: TransactionRelation[],
): Record<string, TransactionRelation | null> => {
  return relations.reduce<Record<string, TransactionRelation | null>>(
    (acc, item) => {
      acc[String(item.trade_id)] = item;

      return acc;
    },
    {},
  );
};

export const mapTargetTransactionsToTrades = (
  targetTransactions: BulkTransactionTitle[],
): Trade[] =>
  targetTransactions.map((targetTransaction) => ({
    tradeId: String(targetTransaction.transaction_id),
    title: targetTransaction.title || '',
    maturity_date: targetTransaction.maturity_date || '',
    companyEntity: targetTransaction.company_entity?.name || '',
    companyEntityId: String(targetTransaction.company_entity?.id || ''),
    bank: targetTransaction.bank_entity?.name || '',
    bankId: String(targetTransaction.bank_entity?.id || ''),
    instrument: String(targetTransaction.instrument_type?.description || ''),
    trade_date: String(targetTransaction.trade_date || ''),
    journalEntries: [],
    hedgeAmortData: [],
    transactionEffectivenessData: [],
    hypotheticalData: [],
  }));
